import React, { useContext, useMemo } from 'react';
import pathToRegexp, {compile} from 'path-to-regexp';
import {stringify as queryStringify} from 'querystring';

const RouteStorageContext = React.createContext({});

export const useRoutes = () => useContext(RouteStorageContext);

export const generateRoute = (routes, routeName, args = {}) => {
    if (!routes[routeName]) {
        throw new Error('Unknown route "' + routeName + '"!');
    }

    // Compile path
    const {compile, pathKeys} = routes[routeName];
    let computedPath = compile(args);

    // Get keys not in path and stringify the object values
    const query = {};
    for (const argKey of Object.keys(args)) {
        if (!pathKeys.includes(argKey)) {
            const argValue = args[argKey];
            if (typeof argValue !== 'object') {
                query[argKey] = argValue;
            } else {
                query[argKey] = JSON.stringify(argValue);
            }
        }
    }

    // Add query string
    const queryString = queryStringify(query);
    if (queryString.length > 0) {
        computedPath += '?' + queryString;
    }

    return computedPath;
};

export const useRoute = (routeName, args = {}) => {
    const routes = useRoutes();

    return useMemo(() => generateRoute(routes, routeName, args), [routes, routeName, args]);
};

export default ({routes, children}) => {
    // Build route info
    const builtRoutes = {};
    for (const routeName of Object.keys(routes)) {
        const {path} = routes[routeName];
        const pathKeys = [];
        // Parse route path
        pathToRegexp(path, pathKeys);

        builtRoutes[routeName] = {
            compile: compile(path, {encode: encodeURIComponent}),
            pathKeys: pathKeys.map(value => value.name),
        };
    }

    return (
        <RouteStorageContext.Provider value={builtRoutes}>
            {children}
        </RouteStorageContext.Provider>
    );
};
